import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  FormControl,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {AllowedUsersModel} from 'library/src/authentication/models/AllowedUsersModel';
import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import IntlMessages from 'shared/components/IntlMessages';
import {AppState} from 'shared/store';
import weatherActions from '../actions/WeatherActions';
import {ConnectedStationType} from '../models/WeatherStationResponse';
import WeatherStationCredentialsForm from './WeatherStationCredentialsForm';

const ModifyWeatherStation: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const {weather_providers} = useSelector<AppState, AppState['weather']>(({weather}) => weather);
  const weatherProviderList = Object.values(weather_providers || {});
  const providerList = weatherProviderList.filter((provider) => provider.provider_type !== ConnectedStationType.MF);
  const {user} = useSelector<AppState, AppState['auth']>(({auth}) => auth);
  const {farmersList} = useSelector<AppState, AppState['fields']>(({fields}) => fields);

  const defaultProducer = farmersList.find((farmer) => farmer.uid === user?.uid) || farmersList[0] || null;

  const [userToModify, setUserToModify] = useState<AllowedUsersModel | null>(defaultProducer);
  const [selectedProviderId, setSelectedProviderId] = useState<number | ''>('');

  useEffect(() => {
    if (!farmersList?.length) return;

    if (!userToModify) {
      setUserToModify(farmersList.find((f) => f.uid === user?.uid) || farmersList[0] || null);
      return;
    }

    const fresh = farmersList.find((f) => f.uid === userToModify.uid) || null;
    setUserToModify(fresh);
  }, [farmersList, user, userToModify]);

  useEffect(() => {
    const id = userToModify?.credentials?.idFournisseur;
    setSelectedProviderId(id ?? '');
  }, [userToModify]);

  const providerSelected = providerList.find((p) => p.provider_id === selectedProviderId) || null;

  const handleDelete = (providerId: number) => {
    if (userToModify) {
      const data = {
        uid: userToModify.uid,
        provider_id: providerId,
      };
      dispatch(weatherActions.deleteUserWeatherStation(data));
    }
  };

  const handleSelectProducer = (producer: AllowedUsersModel) => {
    setUserToModify(producer);
  };
  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: '20px', pt: '20px'}}>
      <Typography variant='body1' color='default'>
        <IntlMessages id='weather.weather_provider_linking' />
      </Typography>
      <Autocomplete
        options={farmersList}
        getOptionLabel={(option) => option.farmer_name}
        onChange={(event, value) => handleSelectProducer(value)}
        value={userToModify || undefined}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <TextField {...params} variant='outlined' label={intl.formatMessage({id: 'fields.filter.farmer.label'})} />
        )}
        renderOption={(props, option) => (
          <ListItem {...props} key={option.farmer_id}>
            <Typography>{option.farmer_name}</Typography>
          </ListItem>
        )}
        disableClearable
        isOptionEqualToValue={(option, value) => option.uid === value.uid}
      />
      <FormControl fullWidth variant='outlined'>
        <InputLabel id='selected-provider-label'>
          <IntlMessages id='weather.weather_provider' />
        </InputLabel>
        <Select
          labelId='selected-provider-label'
          id='selected-provider-select'
          value={selectedProviderId}
          label={intl.formatMessage({id: 'weather.weather_provider'})}
          onChange={(e) => {
            const val = Number(e.target.value);
            setSelectedProviderId(Number.isNaN(val) ? '' : val);
          }}>
          {providerList.map((provider) => (
            <MenuItem value={provider.provider_id} key={provider.provider_id}>
              {provider.provider_name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {providerSelected && userToModify && (
        <WeatherStationCredentialsForm selectedProvider={providerSelected} selectedProducer={userToModify} />
      )}
      <Box display={'flex'} flexDirection={'column'}>
        <Typography variant='body1' color='default'>
          <IntlMessages id='weather.weather_provider.linked_provider_list' />
        </Typography>
        {userToModify?.credentials?.idFournisseur && (
          <Box display={'flex'} flexDirection={'row'} sx={{alignItems: 'center'}}>
            <Typography variant='body1' color='default' fontStyle={'italic'} sx={{height: 'fit-content'}}>
              {providerList.find((p) => p.provider_id === userToModify?.credentials?.idFournisseur)?.provider_name ||
                'Provider inconnu'}
            </Typography>
            <DeleteIcon
              sx={{margin: '10px', cursor: 'pointer'}}
              color='error'
              onClick={() => {
                if (userToModify?.credentials?.idFournisseur) handleDelete(userToModify.credentials.idFournisseur);
              }}
            />
          </Box>
        )}
        {!userToModify?.credentials?.idFournisseur && (
          <Typography
            variant='body1'
            color='default'
            fontStyle={'italic'}
            fontWeight={'bold'}
            sx={{height: 'fit-content', margin: '10px'}}>
            <IntlMessages id='weather.weather_provider.no_linked' />
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ModifyWeatherStation;

/* <Box display={'flex'} flexDirection={'column'}>
      <Typography variant='body1' color='default'>
        <IntlMessages id='weather.weather_provider.linked_provider_list' />
      </Typography>
      {user?.connected_stations_provider &&
        user?.connected_stations_provider.length > 0 &&
        user.connected_stations_provider.map((provider) => {
          return (
            <Box
              key={provider.provider_id}
              display={'flex'}
              flexDirection={'row'}
              sx={{alignItems: 'center'}}>
              <Typography
                variant='body1'
                color='default'
                fontStyle={'italic'}
                sx={{height: 'fit-content'}}>
                {provider.provider_name}
              </Typography>
              <DeleteIcon
                sx={{margin: '10px', cursor: 'pointer'}}
                color='error'
                onClick={() => handleDelete(provider.provider_id)}
              />
            </Box>
          );
        })}
      {!user?.connected_stations_provider ||
        (user?.connected_stations_provider.length === 0 && (
          <Typography
            variant='body1'
            color='default'
            fontStyle={'italic'}
            fontWeight={'bold'}
            sx={{height: 'fit-content', margin: '10px'}}>
            <IntlMessages id='weather.weather_provider.no_linked' />
          </Typography>
        ))}
    </Box>; */
